import 'client-only';

import { logger } from '@curology/logger/client';
import { useAtom } from 'jotai';

import { PaymentsState } from '@/atoms/payments/paymentsState';
import { useCreditCardForm } from '@/components/payments/hooks/useCreditCardForm';
import { useStripeClient } from '@/components/payments/hooks/useStripeClient';

/**
 * The Stripe client can be used to confirm a payment method in the Stripe API.
 * The returned nonce can be used to create and store the payment method in the backend.
 *
 * Wrap the page in the CreditCardClientProvider to ensure the Stripe client context is loaded.
 */
export const useStripe = () => {
  const stripeClient = useStripeClient();
  const [stripeElement] = useAtom(PaymentsState.StripeElementAtom);
  const { ccFormMeta, setCcFormMeta } = useCreditCardForm();

  const createStripeToken = async (
    client: stripe.Stripe,
    card: stripe.elements.Element,
    onError: (message: string) => void,
  ) => {
    try {
      const stripeToken = await client.createToken(card);

      if (stripeToken.error) {
        logger.warn('Create Stripe Token error', {
          metadata: { message: stripeToken.error.message },
          context: 'Next.js Stripe createStripeToken',
        });

        onError(
          stripeToken.error?.message ||
            'Error confirming the payment method. Please try again.',
        );
      }

      return stripeToken.token && !stripeToken.error ? stripeToken : undefined;
    } catch (exception) {
      onError('Error connecting to the payment processor. Please try again.');
      return undefined;
    }
  };

  /**
   * Requests a one-time token response from Stripe.
   *
   * @param onSuccess Executed on a valid one-time token response.
   * @param onError Executed on any error message during the workflow.
   * @param onConfirm Optional. Executed just before confirming.
   */
  const confirmCcPayment = async ({
    onSuccess,
    onError,
    onConfirm = () => {},
  }: {
    onSuccess: (token: stripe.Token) => Promise<void>;
    onError: (message: string) => void;
    onConfirm?: () => void;
  }): Promise<void> => {
    if (!stripeClient || !stripeElement.card) {
      onError(
        'Error loading the credit card processor. Please refresh the page and try again.',
      );
      return;
    }
    const card = stripeElement.card;

    setCcFormMeta({ formSubmitting: true });

    onConfirm();

    const stripeToken = await createStripeToken(stripeClient, card, onError);

    if (stripeToken?.token) {
      await onSuccess(stripeToken.token);
    }

    setCcFormMeta({
      formChanged: false,
      formSubmitting: false,
      tokenResponse: stripeToken,
    });
  };

  return {
    confirmCcPayment,
    canConfirmCcPayment: ccFormMeta.formComplete && !ccFormMeta.formSubmitting,
    ccFormSubmitting: ccFormMeta.formSubmitting,
  };
};
